import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';

/**
 * Load published legal documents for a named flow (registration, recording, etc.)
 * and track per-document checkbox state.
 */
export function useFlowLegalDocuments(flow) {
  const [documents, setDocuments] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!flow) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    api.legal.getFlowDocuments(flow)
      .then(({ data }) => setDocuments(data.documents || []))
      .catch(() => {
        setDocuments([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [flow]);

  useEffect(() => {
    load();
  }, [load]);

  const onCheckChange = useCallback((id, val) => {
    setChecked((prev) => ({ ...prev, [id]: val }));
  }, []);

  const documentIds = useMemo(() => documents.map((d) => d.id), [documents]);

  const allChecked = documents.length > 0
    ? documents.every((d) => checked[d.id])
    : false;

  const canProceed = allChecked && documents.length > 0 && !loading;

  return {
    documents,
    checked,
    setChecked,
    onCheckChange,
    loading,
    error,
    allChecked,
    canProceed,
    documentIds,
    reload: load,
  };
}

export default useFlowLegalDocuments;
